import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { apiClient, API } from "../lib/api";
import { AppHeader } from "../components/AppHeader";
import { Input } from "../components/ui/input";
import { Search, Phone, AlertTriangle, CalendarClock, PlusCircle, Users, Download, Plus } from "lucide-react";
import { Skeleton } from "../components/ui/skeleton";
import { Button } from "../components/ui/button";
import { ContactActions } from "../components/ContactActions";

function isOverdue(dateStr) {
  if (!dateStr) return false;
  const today = new Date().toISOString().slice(0, 10);
  return dateStr < today;
}
function isToday(dateStr) {
  if (!dateStr) return false;
  return dateStr === new Date().toISOString().slice(0, 10);
}
function fmtDate(d) {
  if (!d) return "";
  try {
    const [y, m, day] = d.split("-");
    return `${day}/${m}/${y}`;
  } catch {
    return d;
  }
}

export default function Customers() {
  const [q, setQ] = useState("");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    const t = setTimeout(() => {
      apiClient
        .get(`/customers${q ? `?q=${encodeURIComponent(q)}` : ""}`)
        .then((r) => setItems(r.data))
        .catch(() => setItems([]))
        .finally(() => setLoading(false));
    }, 200);
    return () => clearTimeout(t);
  }, [q]);

  return (
    <>
      <AppHeader title="Customers" subtitle={`${items.length} ${items.length === 1 ? "customer" : "customers"}`} />
      <div className="px-4 pt-4 pb-28 space-y-3">
        <div className="flex gap-2">
          <div className="relative flex-1">
            <Search className="w-5 h-5 absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              data-testid="search-input"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search by name or phone"
              className="h-12 pl-12 rounded-xl text-base bg-white"
            />
          </div>
          <Link to="/customers/new" data-testid="customers-add-btn"
            className="h-12 px-3 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white flex items-center gap-1 font-semibold text-sm">
            <Plus className="w-4 h-4" /> Add
          </Link>
          <a href={`${API}/exports/customers.xlsx`} target="_blank" rel="noreferrer"
            data-testid="export-customers-btn"
            className="h-12 px-3 rounded-xl border border-slate-200 bg-white flex items-center gap-1 font-semibold text-slate-700 text-sm hover:bg-slate-50">
            <Download className="w-4 h-4" /> Excel
          </a>
        </div>

        {loading ? (
          <div className="space-y-2">
            {[1, 2, 3, 4].map((i) => (
              <Skeleton key={i} className="h-20 rounded-2xl" />
            ))}
          </div>
        ) : items.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-2xl border border-dashed border-slate-200">
            <div className="w-14 h-14 rounded-full bg-slate-100 mx-auto grid place-items-center mb-3">
              <Users className="w-7 h-7 text-slate-400" strokeWidth={2.5} />
            </div>
            <p className="text-slate-700 font-semibold mb-1">
              {q ? "No matches found" : "No customers yet"}
            </p>
            <p className="text-sm text-slate-500 mb-4">
              {q ? "Try a different name or phone" : "Add your first customer"}
            </p>
            {!q && (
              <Button asChild className="h-12 px-6 rounded-xl bg-emerald-600 hover:bg-emerald-700">
                <Link to="/customers/new" data-testid="empty-add-btn">
                  <PlusCircle className="w-4 h-4 mr-1" /> Add Customer
                </Link>
              </Button>
            )}
          </div>
        ) : (
          <div className="space-y-2" data-testid="customer-list">
            {items.map((c) => {
              const overdue = isOverdue(c.follow_up);
              const today = isToday(c.follow_up);
              return (
                <Link
                  key={c.id}
                  to={`/customers/${c.id}`}
                  data-testid={`customer-card-${c.id}`}
                  className={`flex items-center gap-3 rounded-2xl p-3 border active:bg-slate-50 ${
                    overdue ? "bg-red-50 border-red-200" : "bg-white border-slate-200"
                  }`}
                >
                  <div
                    className={`w-11 h-11 rounded-full grid place-items-center font-bold text-base shrink-0 ${
                      overdue ? "bg-red-100 text-red-700" : "bg-emerald-100 text-emerald-700"
                    }`}
                  >
                    {c.name.charAt(0).toUpperCase()}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="font-semibold text-slate-900 truncate">{c.name}</div>
                    <div className="text-sm text-slate-500 flex items-center gap-1.5">
                      <Phone className="w-3.5 h-3.5" /> {c.phone}
                    </div>
                    {c.follow_up && (
                      <div className="mt-1 flex flex-wrap gap-1.5">
                        {overdue ? (
                          <span className="inline-flex items-center gap-1 rounded-full bg-red-100 px-2 py-0.5 text-xs font-semibold text-red-700">
                            <AlertTriangle className="w-3 h-3" /> Overdue · {fmtDate(c.follow_up)}
                          </span>
                        ) : today ? (
                          <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-xs font-semibold text-amber-700">
                            <CalendarClock className="w-3 h-3" /> Today
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5 text-xs font-semibold text-slate-600">
                            <CalendarClock className="w-3 h-3" /> Follow-up {fmtDate(c.follow_up)}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                  <ContactActions phone={c.phone} name={c.name} />
                </Link>
              );
            })}
          </div>
        )}
      </div>
    </>
  );
}
